<template>
  <div class="flex-col">
    <div style="display: flex; align-items: center; padding: 0 16px 16px;">
      <AntInputSearch
        v-model:value="searchState.value"
        placeholder="Search"
        @search="onSearch"
        allowClear
      />

      <AntButton
        v-if="!readOnly && type === 'doc'"
        style="margin-left: 16px;"
        type="primary"
        shape="circle"
        size="small"
        title="建立子本"
        @click="onChangeCreateDocModalVisible(true)"
      >
        <template #icon>
          <AddIcon />
        </template>
      </AntButton>
    </div>
    <AntTree
      class="section-tree"
      :expandedKeys="expandedKeys"
      :selectedKeys="selectedKeys"
      :treeData="displayedTreeData"
      :fieldNames="{ key: 'treeKey', title: 'treeTitleWithRemark', children: 'treeChildren' }"
      :draggable="!readOnly && !searchState.value"
      blockNode
      @select="onTreeSelect"
      @expand="onTreeExpand"
      @drop="onDrop"
    >
      <template #title="{ dataRef, treeTitleWithRemark = '', treeKey, treePos, treeLevel }">
        <!-- <AddIcon
          class="menu-item-icon"
          v-if="['info', 'apis'].includes(dataRef.sectionType)"
          :onClick="e => onClickAddSection(e, dataRef)"
        /> -->
        <AntDropdown :trigger="['contextmenu']">
          <div :id="treeKey">
            <span v-if="isDev && dataRef.sortOrder != null" style="color:dodgerblue;">{{ dataRef.sortOrder }}. </span>

            <span :class="{ 'tree-title': type === 'master' ? treeKey.endsWith('master') : treeLevel === 1 }">
              <template v-if="searchState.filtered && treeTitleWithRemark.toLowerCase().indexOf(searchState.text.toLowerCase()) > -1">
                <span>{{ treeTitleWithRemark.substr(0, treeTitleWithRemark.toLowerCase().indexOf(searchState.text.toLowerCase())) }}</span>
                <span style="color: #f50">{{ treeTitleWithRemark.substr(treeTitleWithRemark.toLowerCase().indexOf(searchState.text.toLowerCase()), searchState.text.length) }}</span>
                <span>{{ treeTitleWithRemark.substr(treeTitleWithRemark.toLowerCase().indexOf(searchState.text.toLowerCase()) + searchState.text.length) }}</span>
              </template>
              <template v-else>{{ treeTitleWithRemark }}</template>
            </span>

            <span v-if="isDev">
              <span style="color:DarkGray;"> treeLevel: {{ treeLevel }} | treePos: {{ treePos }}</span> <span style="color:BurlyWood;">{{ treeKey }} </span>
            </span>
          </div>

          <template #overlay>
            <AntMenu>
              <AntMenuItem
                v-if="type === 'doc' && treeLevel === 1"
                @click="onOpenApiDoc(dataRef)"
              >
                <ExportOutlined class="menu-item-icon" /> Open API Doc
              </AntMenuItem>
              <AntMenuItem
                v-if="!readOnly && type === 'master' && treeLevel === 1"
                @click="onAddSection(dataRef)"
              >
                <AddIcon class="menu-item-icon" /> {{ dataRef.sectionType === 'apis' ? 'Add API Section' : 'Add Info Section' }}
              </AntMenuItem>
              <AntMenuItem
                v-if="!readOnly && type === 'master' && treeLevel === 2 && canMoveSectionToPeerWallet(dataRef)"
                @click="onMoveSectionToPeerWallet(dataRef)"
              >
                <SwapOutlined class="menu-item-icon" /> Move to {{ dataRef.wallet === 'multi' ? 'Single' : 'Multi' }} Wallet
              </AntMenuItem>
              <AntMenuItem
                v-if="!readOnly && treeLevel === (type === 'master' ? 2 : 1)"
                @click="onDuplicate(dataRef)"
              >
                <DuplicateIcon class="menu-item-icon" /> Duplicate
              </AntMenuItem>
              <AntMenuItem
                v-if="!readOnly && treeLevel === (type === 'master' ? 2 : 1) && !['allwecan.info#multi#PRD', 'allwecan.info#single#PRD'].includes(dataRef.docId)"
                @click="onDelete(dataRef)"
              >
                <DeleteIcon class="menu-item-icon" /> Delete
              </AntMenuItem>
            </AntMenu>
          </template>
        </AntDropdown>
      </template>
    </AntTree>
    <div v-if="isDev">
      <div style="display: flex;">
        <DevDebugDataBlock title="expandedKeys" :data="expandedKeys" maxHeight="100px" />
        <DevDebugDataBlock title="selectedKeys" :data="selectedKeys" maxHeight="100px" />
        <DevDebugDataBlock title="treeNodes" :data="treeNodes" maxHeight="100px" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  h,
  inject,
  reactive,
  ref,
  watch,
  computed,
  nextTick,
} from 'vue'

import { useRouter, useRoute } from 'vue-router'

import {
  cloneDeep,
  isEmpty,
  isEqual,
  capitalize,
} from 'lodash'

import ShortUniqueId from 'short-unique-id'

import {
  Tree as AntTree,
  Modal as AntModal,
  Button as AntButton,
  Input as AntInput,
  Menu as AntMenu,
  Tag as AntTag,
  Dropdown as AntDropdown,
  message as antMessage,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'
import DuplicateIcon from '@ant-design/icons-vue/lib/icons/CopyOutlined'
import SwapOutlined from '@ant-design/icons-vue/lib/icons/SwapOutlined'
import ExportOutlined from '@ant-design/icons-vue/lib/icons/ExportOutlined'

import {
  createSection,
  createDoc,
  duplicateDoc,
  deleteDoc,
  deleteSection,
  duplicateSection,
  moveSectionToPeerWallet,
  batchWriteSections,
} from '@/api/apiDocV3'

import DevDebugDataBlock from '@/components/ApiDoc/shared/DevDebugDataBlock.vue'

import { titleCase } from '@/utils'

import {
  PARENT_SECTION_ROOT,
  isRootParentSectionId,
} from '../lib/constants'

import {
  getDocTitle,
  getDocShortTitle,
  transformToNested,
  findTreeAndKeys,
  buildSectionTreeTitles,
  updateSectionsBySectionIdSortOrderByTreeData,
  stripChildCustomizedSortOrdersForSectionIds,
  syncTreeNodesSortOrderFromSections,
  rebuildWalletDocSectionTrees,
  applyDocSortOrdersFromTreeChildren,
  normalizeDocRootSortOrdersContiguous,
  buildSortedSectionsListForDoc,
  buildDocSectionTreeChildrenForDoc,
  rebuildDocSectionTreeChildrenPreservingOrder,
  upsertDraftSectionEntries,
  getUpdatedDocIds,
  sortDocIds,
  cleanSection,
  deriveDocDataWithTitlesAndTemplateData,
  getApiDocLink,
} from '../lib/libsV3'

export default {
  name: 'SectionTree',
  components: {
    DevDebugDataBlock,
    AntTree,
    AntButton,
    AntMenu,
    AntMenuItem: AntMenu.Item,
    AntInputSearch: AntInput.Search,
    AntDropdown,
    AddIcon,
    DeleteIcon,
    DuplicateIcon,
    SwapOutlined,
    ExportOutlined,
  },
  props: {
    readOnly: Boolean,
    type: String,
    activeTreeTab: String,
    autoSelectDraftSection: Boolean,
    isDataChanged: Boolean,
    sectionsDataByDocId: Object,
    sectionsByWallet: Object,
    originSectionsByWallet: Object,
    fieldsByWallet: Object,
    originFieldsByWallet: Object,
    docsDataByDocId: Object,
    templateDataByTemplateId: Object,
    releaseVerSectionByDocId: Object,
    releaseVerFieldsByDocId: Object,
    draftSection: Object,
    draftSectionsByWallet: Object,
    handleWriteDraftSection: Function,
    selectedSectionState: Object,
    onSelectSectionState: Function,
    showPublishDraftModal: Function,
    showSaveChangesModal: Function,
    onChangeSpinVisible: Function,
    onChangeCreateDocModalVisible: Function,
  },
  emits: ['changeActiveTreeTab', 'changeSectionsDataByDoc', 'changeDocsDataByDocId'],
  setup(props, { emit, expose }) {
    const isDev = inject('isDev')

    const router = useRouter()
    const route = useRoute()

    const searchState = reactive({
      value: '',
      text: '',
      filtered: false,
    })

    const expandedKeys = ref([`multi#${props.type}`, `single#${props.type}`])
    const selectedKeys = ref(props.selectedSectionState.treeKey ? [props.selectedSectionState.treeKey] : [])
    const treeNodes = ref([])

    const draftSectionsByWallet = reactive(props.draftSectionsByWallet)
    const sectionsByWallet = reactive(props.sectionsByWallet)
    const originSectionsByWallet = reactive(props.originSectionsByWallet)
    const fieldsByWallet = reactive(props.fieldsByWallet)
    const originFieldsByWallet = reactive(props.originFieldsByWallet)
    const releaseVerSectionByDocId = reactive(props.releaseVerSectionByDocId)
    const releaseVerFieldsByDocId = reactive(props.releaseVerFieldsByDocId)
    const getReleaseVerSectionForDoc = docId => props.releaseVerSectionByDocId?.[docId]

    // onUseDraft => 自動點選到 updatedDataBySectionId[0]
    watch(() => props.autoSelectDraftSection, async (newVal) => {
      console.log('[SectionTree] watch props.autoSelectDraftSection', {
        newVal, draftSection: props.draftSection, activeTreeTab: props.activeTreeTab, type: props.type,
      })
      if (!newVal || props.activeTreeTab !== props.type) return

      const { wallet } = props.draftSection
      const updatedDocIds = getUpdatedDocIds(props.draftSection)
      const docId = updatedDocIds[0] || props.draftSection.docId
      const firstUpdatedSectionId = Object.keys(props.draftSection.updatedDataBySectionId || {})[0]
      if (firstUpdatedSectionId) {
        if (!expandedKeys.value.includes(docId)) {
          document.getElementById(docId).click()
          await nextTick()
        }

        const { parentSectionId, sectionId } = sectionsByWallet[wallet][firstUpdatedSectionId]
        const parentKey = `${docId}-${parentSectionId}`
        if (!isRootParentSectionId(parentSectionId) && !expandedKeys.value.includes(parentKey)) {
          document.getElementById(parentKey).click()
          await nextTick()
        }

        const sectionEl = document.getElementById(`${docId}-${sectionId}`)
        if (sectionEl) {
          sectionEl.click()
          sectionEl.scrollIntoView()
        }
      } else { // 子本只有更新 draftSection.updatedDoc 時
        // eslint-disable-next-line no-lonely-if
        if (!expandedKeys.value.includes(docId)) {
          document.getElementById(docId).click()
          await nextTick()
        }

        const docEl = document.getElementById(docId)
        if (docEl) {
          docEl.click()
          docEl.scrollIntoView()
        }
      }
    })

    const masterDir = {
      multi: {
        treeTitle: 'Multi Wallet',
        treeTitleWithRemark: 'Multi Wallet',
        treeKey: 'multi#master',
        treePos: 0,
        treeLevel: 0,
        docId: 'multi#master',
        treeChildren: [],
      },
      single: {
        treeTitle: 'Single Wallet',
        treeTitleWithRemark: 'Single Wallet',
        treeKey: 'single#master',
        treePos: 1,
        treeLevel: 0,
        docId: 'single#master',
        treeChildren: [],
      },
    }

    const transformToMasterTree = (updateSortOrder) => {
      ['multi', 'single'].forEach((wallet) => {
        Object.values(sectionsByWallet[wallet]).forEach((section) => {
          const { docId, sectionId } = section
          masterDir[wallet].treeChildren.push({
            ...section,
            treeKey: `${docId}-${sectionId}`,
          })
        })
      })

      return Object.values(masterDir).map((dirItem) => {
        // console.log('wallet.treeChildren', wallet.treeChildren)
        const {
          treeChildren,
          treePos,
          // treeLevel,
        } = dirItem

        if (treeChildren) {
          dirItem.treeChildren = transformToNested({
            data: treeChildren,
            treeLevel: 1,
            posPrefix: treePos,
            updateSortOrder,
          })
        }
        return dirItem
      })
    }

    const docDir = {
      multi: {
        treeTitle: 'Multi Wallet',
        treeTitleWithRemark: 'Multi Wallet',
        treeKey: 'multi#doc',
        treePos: 0,
        treeLevel: 0,
        treeChildren: {},
      },
      single: {
        treeTitle: 'Single Wallet',
        treeTitleWithRemark: 'Single Wallet',
        treeKey: 'single#doc',
        treePos: 1,
        treeLevel: 0,
        treeChildren: {},
      },
    }

    const transformToDocTree = (updateSortOrder) => {
      console.log('transformToDocTree props.docsDataByDocId', props.docsDataByDocId)

      const docIds = new Set([
        ...Object.keys(props.sectionsDataByDocId || {}),
        ...Object.keys(props.docsDataByDocId || {}),
      ])

      docIds.forEach((docId) => {
        if (docId.includes('master')) return

        const wallet = docId.split('#')[1]
        if (!docDir[wallet]) return

        if (!docDir[wallet].treeChildren[docId]) {
          const docData = props.docsDataByDocId[docId]
          docDir[wallet].treeChildren[docId] = {
            docId,
            ...docData,
            treeTitle: docData?.docShortTitle,
            treeKey: docId,
            treePos: `${docDir[wallet].treePos}-${Object.keys(docDir[wallet].treeChildren).length}`,
            treeLevel: 1,
            treeChildren: [],
          }
        }
      })

      return Object.values(docDir).map((wallet) => {
        const sortedDocIds = sortDocIds(Object.keys(wallet.treeChildren))

        wallet.treeChildren = sortedDocIds.map((docId) => {
          const doc = wallet.treeChildren[docId]
          const w = docId.split('#')[1]

          doc.treeChildren = buildDocSectionTreeChildrenForDoc({
            docId,
            sectionsBySectionId: sectionsByWallet[w],
            releaseVerSection: getReleaseVerSectionForDoc(docId),
            treeLevel: (doc.treeLevel ?? 1) + 1,
            posPrefix: doc.treePos,
            updateSortOrder,
          })

          return doc
        })
        return wallet
      })
    }

    const treeData = ref([])
    const displayedTreeData = ref([])

    /** 子本樹新增 doc 節點後依 sortDocIds 重排（與 transformToDocTree 一致） */
    const sortWalletDocTreeChildren = (wallet) => {
      const dirIdx = docDir[wallet]?.treePos
      const children = treeData.value[dirIdx]?.treeChildren
      if (!Array.isArray(children) || children.length < 2) return

      const byDocId = Object.fromEntries(
        children.map(node => [node.treeKey || node.docId, node]),
      )
      const sortedIds = sortDocIds([...Object.keys(byDocId)])
      const posPrefix = docDir[wallet].treePos

      // 勿改寫 docDir[wallet].treeChildren 為 object：transformToDocTree 後與 treeData 為同一引用，會導致 Ant Tree children.forEach 報錯
      treeData.value[dirIdx].treeChildren = sortedIds.map((id, idx) => ({
        ...byDocId[id],
        treePos: `${posPrefix}-${idx}`,
      }))
    }

    const getWalletDocTreeChildren = (wallet) => {
      const dirIdx = docDir[wallet]?.treePos
      const children = treeData.value[dirIdx]?.treeChildren
      return Array.isArray(children) ? children : []
    }

    /** 從目標 section 往上走，取得展開路徑所需的 ancestor treeKey（子節點出現前須先展開父節點） */
    const collectAncestorKeysForMasterRoutePath = (wallet, sectionId, sectionsMap) => {
      const keys = []
      if (!sectionsMap || !sectionId) return keys
      let cur = sectionsMap[sectionId]
      while (cur && !isRootParentSectionId(cur.parentSectionId)) {
        keys.push(`${wallet}#master-${cur.parentSectionId}`)
        cur = sectionsMap[cur.parentSectionId]
      }
      return keys
    }

    /** 由 treeData 找節點路徑（與 onClickTreeItem 的 parent.nodes 麵包屑一致） */
    const collectTreeNodesPathFromTreeData = (nodes, targetTreeKey, path = []) => {
      if (!nodes?.length || !targetTreeKey) return null
      for (const node of nodes) {
        const nextPath = [...path, node.treeTitleWithRemark]
        if (node.treeKey === targetTreeKey) return nextPath
        const childPath = collectTreeNodesPathFromTreeData(node.treeChildren, targetTreeKey, nextPath)
        if (childPath) return childPath
      }
      return null
    }

    /** URL / 程式選取 section 時同步 treeNodes（不依賴樹節點 click） */
    const buildMasterTreeNodesFromSectionsMap = (wallet, sectionId, sectionsMap) => {
      if (!sectionsMap?.[sectionId]) return []
      const titles = [masterDir[wallet].treeTitleWithRemark]
      const chain = []
      let cur = sectionsMap[sectionId]
      while (cur) {
        chain.unshift(cur)
        if (isRootParentSectionId(cur.parentSectionId)) break
        cur = sectionsMap[cur.parentSectionId]
      }
      let posPrefix = String(masterDir[wallet].treePos)
      let parentDisplayNo = null
      chain.forEach((section) => {
        const siblings = Object.values(sectionsMap)
          .filter(s => s.parentSectionId === section.parentSectionId)
          .sort((a, b) => a.sortOrder - b.sortOrder)
        const index = siblings.findIndex(s => s.sectionId === section.sectionId)
        titles.push(buildSectionTreeTitles(section, index, posPrefix, true, parentDisplayNo).treeTitleWithRemark)
        parentDisplayNo = isRootParentSectionId(section.parentSectionId)
          ? index + 1
          : section.sortOrder
        posPrefix = `${posPrefix}-${index}`
      })
      return titles
    }

    const syncTreeNodesForMasterSection = (wallet, sectionId) => {
      if (props.type !== 'master' || !wallet || !sectionId) return
      const sectionKey = `${wallet}#master-${sectionId}`
      const fromTree = collectTreeNodesPathFromTreeData(treeData.value, sectionKey)
      treeNodes.value = fromTree?.length
        ? fromTree
        : buildMasterTreeNodesFromSectionsMap(wallet, sectionId, sectionsByWallet[wallet])
    }

    // 在 await nextTick() 之後用 requestAnimationFrame 輪詢（最多約 3 秒），等 Vue 與 Tree 把子節點掛好再 scrollIntoView。
    // 不可對節點 programmatic click()：拖曳排序後 sectionsByWallet 會觸發 watch → 本函式若 click 會再走 onTreeSelect → onClickTreeItem，
    // 在 isDataChanged 時會誤跳「有尚未儲存的變更，確定要捨棄？」（使用者並未改選區塊）。
    async function waitForElementById(id, { maxMs = 3000 } = {}) {
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        const el = document.getElementById(id)
        if (el) return el
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return null
    }

    /**
     * 先寫入 expandedKeys（Ant Tree 未展開時子節點不會掛載，getElementById 永遠為 null），
     * 再等 Vue + Tree 完成渲染後再 scrollIntoView（勿對 DOM click，理由見 waitForElementById 註解）。
     */
    async function expandRouteAndScrollToMasterSection(treeWallet) {
      if (props.type !== 'master') return
      // 編輯子本或作用中為子本 tab 時，sectionsByWallet 變更（如一鍵加入 Used In）不應依 URL 覆寫 selectedSectionState
      if (props.selectedSectionState?.docTitle || props.activeTreeTab !== 'master') return
      const routeWallet = route.params.wallet
      const routeSectionId = route.params.sectionId
      console.log('expandRouteAndScrollToMasterSection', {
        treeWallet,
        routeWallet,
        routeSectionId,
        'selectedSectionState.wallet': props.selectedSectionState.wallet,
        'selectedSectionState.sectionId': props.selectedSectionState.sectionId,
      })
      // 不可因 selectedSectionState 已與 route 相同就 return：從子本樹點選時會先 onSelectSectionState 再 router.push，
      // 若此處略過則母本樹的 expandedKeys / selectedKeys 不會更新。
      if (!routeWallet || !routeSectionId || treeWallet !== routeWallet) return
      const sectionData = sectionsByWallet[routeWallet]?.[routeSectionId]
      if (!sectionData) {
        router.push('/api-doc/editor')
        return
      }
      const { wallet: selectedWallet, sectionId: selectedSectionId } = props.selectedSectionState
      if (selectedWallet !== routeWallet || selectedSectionId !== routeSectionId) {
        props.onSelectSectionState(sectionData)
      }
      if (searchState.filtered) {
        searchState.value = ''
        searchState.text = ''
        searchState.filtered = false
        displayedTreeData.value = treeData.value
      }
      const sectionsMap = sectionsByWallet[routeWallet]
      const ancestorKeys = collectAncestorKeysForMasterRoutePath(routeWallet, routeSectionId, sectionsMap)
      const rootKey = `${routeWallet}#master`
      const sectionKey = `${routeWallet}#master-${routeSectionId}`
      expandedKeys.value = [...new Set([...expandedKeys.value, rootKey, ...ancestorKeys])]
      selectedKeys.value = [sectionKey]
      syncTreeNodesForMasterSection(routeWallet, routeSectionId)
      console.log('expandRouteAndScrollToMasterSection ancestorKeys', ancestorKeys, 'expandedKeys', expandedKeys.value, 'treeNodes', treeNodes.value)
      await nextTick()
      // treeData 剛建立時可能尚無巢狀標題，再試一次
      if (!treeNodes.value.length) syncTreeNodesForMasterSection(routeWallet, routeSectionId)

      const sectionEl = await waitForElementById(sectionKey)
      if (sectionEl) {
        sectionEl.scrollIntoView({ block: 'nearest' })
      }
    }

    const firstLoad = true
    const watchDocSectionsData = (docId) => {
      const wallet = docId.split('#')[1]
      console.log('watchDocSectionsData docId', docId, 'wallet', wallet)

      watch(
        () => ({
          sections: props.sectionsDataByDocId[docId],
          releaseVer: getReleaseVerSectionForDoc(docId),
        }),
        ({ sections }) => {
          console.log(`[SectionTree] watch sectionsDataByDocId ${docId}`, sections)
          const dirIdx = docDir[wallet].treePos
          const walletDocTreeChildren = getWalletDocTreeChildren(wallet)
          const docIdx = walletDocTreeChildren.findIndex(({ treeKey }) => treeKey === docId)
          const posPrefix = docIdx > -1
            ? walletDocTreeChildren[docIdx].treePos
            : `${docDir[wallet].treePos}-${treeData.value[dirIdx]?.treeChildren?.length ?? 0}`

          const rebuildDocSectionTree = () => buildDocSectionTreeChildrenForDoc({
            docId,
            sectionsBySectionId: sectionsByWallet[wallet],
            releaseVerSection: getReleaseVerSectionForDoc(docId),
            treeLevel: 2,
            posPrefix,
            updateSortOrder: false,
          })

          const hasSectionList = Array.isArray(sections)
            ? sections.length > 0
            : Boolean(sections && Object.keys(sections).length)
          const docMeta = props.docsDataByDocId?.[docId]

          // 僅在 doc 已刪除時移除節點；新建子本尚無 section.docs 引用時仍顯示標題（子節點可僅有 ReleaseVer）
          if (!hasSectionList) {
            if (!docMeta) {
              if (docIdx > -1) {
                treeData.value[dirIdx].treeChildren.splice(docIdx, 1)
                displayedTreeData.value = treeData.value
              }
              return
            }

            const emptyDocChildren = rebuildDocSectionTree()
            if (docIdx > -1) {
              const docNode = treeData.value[dirIdx].treeChildren[docIdx]
              docNode.treeChildren = emptyDocChildren
              docNode.treeTitle = docMeta.docShortTitle
              docNode.treeTitleWithRemark = docMeta.docShortTitle
            } else {
              const newDocNode = {
                docId,
                ...docMeta,
                treeTitle: docMeta.docShortTitle,
                treeTitleWithRemark: docMeta.docShortTitle,
                treeKey: docId,
                treePos: posPrefix,
                treeLevel: 1,
                treeChildren: emptyDocChildren,
              }
              treeData.value[dirIdx].treeChildren.push(newDocNode)
              sortWalletDocTreeChildren(wallet)
            }
            displayedTreeData.value = treeData.value
            return
          }

          if (!treeData.value.length) {
            treeData.value = transformToDocTree(firstLoad)
            displayedTreeData.value = treeData.value
            return
          }

          if (docIdx > -1) {
            const sectionsList = buildSortedSectionsListForDoc(docId, sectionsByWallet[wallet])
            normalizeDocRootSortOrdersContiguous({
              docId,
              sectionsList,
              sectionsBySectionId: sectionsByWallet[wallet],
            })
            treeData.value[dirIdx].treeChildren[docIdx].treeChildren = rebuildDocSectionTree()
          } else { // handleCreateDoc / handleDuplicateDoc
            console.log('**********props.docsDataByDocId', props.docsDataByDocId)
            const newDocNode = {
              docId,
              ...props.docsDataByDocId[docId],
              treeTitle: props.docsDataByDocId[docId]?.docShortTitle,
              treeTitleWithRemark: props.docsDataByDocId[docId]?.docShortTitle,
              treeKey: docId,
              treePos: posPrefix,
              treeLevel: 1,
              treeChildren: rebuildDocSectionTree(),
            }
            treeData.value[dirIdx].treeChildren.push(newDocNode)
            sortWalletDocTreeChildren(wallet)
          }

          displayedTreeData.value = treeData.value
          console.log('treeData.value', treeData.value)
        },
        { deep: true, immediate: true },
      )
    }

    const watchDocDataToUpdateTreeDocData = (docId) => {
      const wallet = docId.split('#')[1]

      watch(() => props.docsDataByDocId[docId], (newDocData) => {
        console.log(`[SectionTree] watch docsDataByDocId ${docId}`, {
          newDocData,
        })

        const dirIdx = docDir[wallet].treePos
        const walletDocTreeChildren = getWalletDocTreeChildren(wallet)
        const docIdx = walletDocTreeChildren.findIndex(({ treeKey }) => treeKey === docId)

        if (!newDocData) {
          if (docIdx > -1) {
            treeData.value[dirIdx].treeChildren.splice(docIdx, 1)
            displayedTreeData.value = treeData.value
          }
          return
        }

        if (docIdx > -1) {
          treeData.value[dirIdx].treeChildren[docIdx].treeTitle = newDocData.docShortTitle
          treeData.value[dirIdx].treeChildren[docIdx].treeTitleWithRemark = newDocData.docShortTitle;

          ['subName', 'remark', 'currency', 'platforms', 'excludedPlatforms'].forEach((key) => {
            if (newDocData[key] !== undefined) treeData.value[dirIdx].treeChildren[docIdx][key] = newDocData[key]
          })

          displayedTreeData.value = treeData.value
        }
      }, { deep: true, immediate: true })
    }

    if (props.type === 'master') {
      ['multi', 'single'].forEach((wallet) => {
        watch(() => sectionsByWallet[wallet], async (newVal) => {
          if (isEmpty(newVal)) return
          console.log(`[SectionTree] watch sectionsByWallet[${wallet}]`, newVal)
          if (!treeData.value.length) {
            treeData.value = transformToMasterTree(firstLoad)
            displayedTreeData.value = treeData.value
            await expandRouteAndScrollToMasterSection(wallet)
            return
          }

          const dirIdx = masterDir[wallet].treePos
          treeData.value[dirIdx].treeChildren = transformToNested({
            data: Object.values(newVal),
            treeLevel: 1,
            posPrefix: masterDir[wallet].treePos,
            updateSortOrder: firstLoad,
          })
          displayedTreeData.value = treeData.value

          await nextTick()
          await expandRouteAndScrollToMasterSection(wallet)
        }, { deep: true, immediate: true })
      })
    } else {
      const docIdsForTreeWatch = new Set([
        ...Object.keys(props.sectionsDataByDocId || {}),
        ...Object.keys(props.docsDataByDocId || {}),
      ])
      docIdsForTreeWatch.forEach((docId) => {
        if (docId.includes('master')) return
        watchDocSectionsData(docId)
        watchDocDataToUpdateTreeDocData(docId)
      })

      // ReleaseVer 常晚於 sectionsDataByDocId 載入；單獨監聽 props 以補建子本樹首節點
      watch(
        () => props.releaseVerSectionByDocId,
        () => {
          if (!treeData.value.length) return;
          ['multi', 'single'].forEach((wallet) => {
            const dirIdx = docDir[wallet]?.treePos
            const walletRoot = treeData.value[dirIdx]
            if (!walletRoot?.treeChildren?.length) return
            walletRoot.treeChildren.forEach((docNode) => {
              const docId = docNode.docId || docNode.treeKey
              if (!docId || docId.includes('master')) return
              const releaseVer = getReleaseVerSectionForDoc(docId)
              if (!releaseVer) return
              const hasReleaseVerNode = docNode.treeChildren?.[0]?.sectionId === 'ReleaseVer'
              if (hasReleaseVerNode) return
              docNode.treeChildren = buildDocSectionTreeChildrenForDoc({
                docId,
                sectionsBySectionId: sectionsByWallet[wallet],
                releaseVerSection: releaseVer,
                treeLevel: (docNode.treeLevel ?? 1) + 1,
                posPrefix: docNode.treePos,
                updateSortOrder: false,
              })
            })
            displayedTreeData.value = treeData.value
          })
        },
        { deep: true },
      )
    }

    // 點選母本任一 section > 點選子本 tab > 點選子本任一 section > 點回母本 tab > 母本 selectedKeys 需被清空
    watch(() => props.selectedSectionState.docId, (newVal, oldVal) => {
      console.log('[SectionTree] watch props.selectedSectionState.docId', {
        newVal, oldVal, activeTreeTab: props.activeTreeTab, type: props.type,
      })
      if (['template', 'platform', 'guide'].includes(newVal)) {
        selectedKeys.value = []
        router.push('/api-doc/editor')
        return
      }
      // 僅清空非作用中 tab 的選取；不可在此 router.push，否則母本樹點 section 後會被子本樹 watch 導回 /api-doc/editor 造成整頁重載感
      if (newVal !== oldVal && props.activeTreeTab !== props.type) {
        selectedKeys.value = []
      }
    })

    watch(
      [() => route.params.wallet, () => route.params.sectionId],
      async ([newWallet, newSectionId], [oldWallet, oldSectionId]) => {
        if (props.type !== 'master') return
        if (!newWallet || !newSectionId) return
        if (newWallet === oldWallet && newSectionId === oldSectionId) return

        if (props.activeTreeTab !== 'master') {
          emit('changeActiveTreeTab', 'master')
          await nextTick()
        }
        await expandRouteAndScrollToMasterSection(newWallet)
      },
    )

    const onSearch = () => {
      searchState.text = searchState.value

      if (searchState.value) {
        searchState.filtered = true

        const findResult = findTreeAndKeys(treeData.value, searchState.text)
        console.log('findResult', findResult)

        expandedKeys.value = findResult.keys
        displayedTreeData.value = findResult.nestedTree
      } else {
        searchState.filtered = false
        displayedTreeData.value = treeData.value
      }
    }

    const onTreeExpand = (keys, { expanded, node }) => {
      console.log('onTreeExpand keys', keys, 'expanded', expanded, node)
      expandedKeys.value = keys
    }

    const onClickTreeItem = async (selectedKey, { node }) => {
      console.trace('onClickTreeItem selectedKey', selectedKey, node)

      const isReselectingCurrentSection = () => {
        const { wallet, sectionId } = node.dataRef
        return wallet === props.selectedSectionState.wallet
          && sectionId === props.selectedSectionState.sectionId
      }

      const shouldShowSpin = !isReselectingCurrentSection()
      if (shouldShowSpin) {
        props.onChangeSpinVisible(true)
        await nextTick()
        await new Promise(resolve => setTimeout(resolve, 0))
      }

      const hideSpin = async () => {
        if (!shouldShowSpin) return
        await nextTick()
        props.onChangeSpinVisible(false)
      }

      const onChangeSection = async () => {
        try {
          console.log('node.parent?.nodes', node.parent?.nodes)

          treeNodes.value = [
            ...(node.parent?.nodes || []).map(({ treeTitleWithRemark }) => treeTitleWithRemark),
            node.treeTitleWithRemark,
          ]
          selectedKeys.value = [selectedKey]
          expandedKeys.value = [...expandedKeys.value, selectedKey]
          console.log('onClickTreeItem treeNodes', treeNodes.value, selectedKeys.value, expandedKeys.value)

          const { wallet, sectionId } = node.dataRef

          if (sectionId === 'ReleaseVer' || node.dataRef.docTitle) {
            props.onSelectSectionState(node.dataRef)

            if (route.params.wallet && route.params.sectionId && route.path !== '/api-doc/editor') {
              router.push('/api-doc/editor')
            }
            return
          }

          // 不使用 onSelectSectionState(node.dataRef) 是因為 sectionsDataByDocId 內的 section 不包含 docs & customizedVersion
          if (!props.onSelectSectionState(sectionsByWallet[wallet][sectionId])) return

          const targetPath = `/api-doc/editor/${wallet}/${sectionId}`
          if (route.path !== targetPath) {
            router.push(targetPath)
          }
        } finally {
          await hideSpin()
        }
      }

      if (props.isDataChanged && !isReselectingCurrentSection()) {
        const modal = AntModal.confirm({
          title: '有尚未儲存的變更，確定要捨棄？',
          content: '',
          okText: 'Cancel',
          cancelText: 'OK',
          onOk: () => {
            modal.destroy()
            hideSpin()
          },
          onCancel: onChangeSection,
        })
      } else {
        await onChangeSection()
      }
    }

    const onTreeSelect = async ([selectedKey], e) => {
      const { treeKey, wallet } = e.node.dataRef
      const resolvedSelectedKey = selectedKey || treeKey
      console.log('[ApiDocSectionEditor] onTreeSelect selectedKey', selectedKey, 'resolvedSelectedKey', resolvedSelectedKey, e)
      if (!resolvedSelectedKey) return

      if (['multi#master', 'single#master', 'multi#doc', 'single#doc'].includes(treeKey)) {
        if (!expandedKeys.value.includes(treeKey)) {
          expandedKeys.value = [...expandedKeys.value, treeKey]
        } else {
          expandedKeys.value = expandedKeys.value.filter(key => key !== treeKey)
        }
        return
      }

      if (!expandedKeys.value.includes(treeKey)) {
        expandedKeys.value = [...expandedKeys.value, treeKey]
      }

      const { docId: draftDocId, wallet: draftWallet } = props.draftSection

      const walletIsDifferentFromDraft = draftDocId && draftWallet && draftWallet !== wallet
      if (walletIsDifferentFromDraft && !isEmpty(props.draftSection.updatedDataBySectionId)) {
        console.log('props.draftSection', props.draftSection, 'props.docsDataByDocId', props.docsDataByDocId)
        props.showPublishDraftModal()
        return
      }

      await onClickTreeItem(resolvedSelectedKey, e)
    }

    const handleCreateSection = async (createSectionData) => {
      props.onChangeSpinVisible(true)

      const newSection = cloneDeep(createSectionData)
      delete newSection.isNew
      delete newSection.treeTitle
      delete newSection.treeTitleWithRemark
      delete newSection.treeTitleNo
      delete newSection.treeEnTitle
      delete newSection.treeCnTitle
      delete newSection.treeKey
      delete newSection.treePos
      delete newSection.treeLevel
      delete newSection.treeChildren
      delete newSection.appliedDocs
      delete newSection.updatedTime
      delete newSection.editorUsername

      const messageKey = Date.now()
      antMessage.loading('', { key: messageKey })

      const res = await createSection(newSection)
      originSectionsByWallet[newSection.wallet][newSection.sectionId] = newSection
      sectionsByWallet[newSection.wallet][newSection.sectionId] = cloneDeep(newSection)

      props.onChangeSpinVisible(false)

      const { data, message } = res
      if (data) {
        antMessage.success({
          key: messageKey,
          content: `Create new section [${newSection.enTitle} ${newSection.cnTitle}] successfully`,
        })
      } else {
        antMessage.error({
          key: messageKey,
          duration: 5,
          content: `Failed to create new section [${newSection.enTitle} ${newSection.cnTitle}]: ${message}`,
        })
      }
      return res
    }

    const onAddSection = async (dataRef) => {
      console.log('onAddSection', dataRef)
      const { wallet, sectionType } = dataRef
      const newSectionId = new ShortUniqueId({ length: 10 }).rnd()
      const sortOrder = dataRef.treeChildren?.length || 0
      const defaultSection = {
        enTitle: 'New Section',
        cnTitle: '新區塊',
        sectionId: newSectionId,
        parentSectionId: dataRef.sectionId,
        docId: dataRef.docId,
        docs: [],
        wallet,
        sectionType,
        ...(dataRef.tag && { tag: dataRef.tag }),
        sortOrder,
      }

      if (sectionType === 'appendices') {
        defaultSection.sectionType = 'appendix'
      }

      if (sectionType === 'apis') {
        defaultSection.sectionType = 'api'

        const newExampleId = new ShortUniqueId({ length: 10 }).rnd()
        defaultSection.examples = {}
        defaultSection.examples[newExampleId] = {
          exampleId: newExampleId,
          exampleName: 'success',
        }
      }

      const { data } = await handleCreateSection(defaultSection)
      console.log('onAddSection defaultSection', defaultSection)

      if (data) {
        nextTick(() => {
          const el = document.getElementById(`${data.docId}-${data.sectionId}`)
          if (el) el.click()
        })
      }
    }

    const onDuplicateSection = async (dataRef) => {
      console.log('onDuplicateSection dataRef', dataRef)
      const newSectionId = new ShortUniqueId({ length: 10 }).rnd()
      const [dirIdx, parentIdx] = dataRef.treePos.split('-')
      const sortOrder = treeData.value[dirIdx].treeChildren[parentIdx].treeChildren.length

      const newSection = cleanSection({
        ...dataRef,
        sectionId: newSectionId,
        enTitle: `Copy ${dataRef.enTitle}`,
        cnTitle: `复制 ${dataRef.cnTitle}`,
        docs: [], // 為了可以走草稿、發布版本流程，先重置
        sortOrder,
      })
      delete newSection.customizedVersion // 為了可以走草稿、發布版本流程，先移除

      const sourceSectionId = dataRef.sectionId
      const { data, message } = await duplicateSection({ sourceSectionId, ...newSection })
      if (data) {
        const { sectionData, fieldsToUpdate } = data
        const { wallet, sectionId } = sectionData // sectionId = newSectionId

        originSectionsByWallet[wallet][sectionId] = cloneDeep(sectionData)

        // 幫 user 加回 docs & customizedVersion，再走草稿、發布版本流程
        sectionData.docs = dataRef.docs
        if (dataRef.customizedVersion) sectionData.customizedVersion = dataRef.customizedVersion
        sectionsByWallet[wallet][sectionId] = sectionData
        console.log('onDuplicateSection sectionData', sectionData)

        await nextTick()

        // 更新 customizedVersion[docId].sortOrder
        const changedSectionIdSet = new Set()
        const walletRoot = treeData.value[masterDir[wallet].treePos]
        if (walletRoot?.treeChildren?.length) {
          updateSectionsBySectionIdSortOrderByTreeData(walletRoot.treeChildren, sectionsByWallet[wallet])
            .forEach(sId => changedSectionIdSet.add(sId))
        }

        const customizedVersionDocIds = Object.keys(sectionData.customizedVersion || {}).filter(d => !d.includes('master'))
        const withDocIdOnTree = (nodes, docId) => nodes.map(node => ({
          ...node,
          docId,
          ...(node.treeChildren?.length && { treeChildren: withDocIdOnTree(node.treeChildren, docId) }),
        }))

        customizedVersionDocIds.forEach((docId) => {
          const docSections = props.sectionsDataByDocId?.[docId]
          if (!docSections?.length) return

          const dataForDoc = docSections.map((s) => {
            const live = sectionsByWallet[wallet][s.sectionId] || s
            const eff = live.customizedVersion?.[docId]?.sortOrder ?? live.sortOrder
            return { ...live, sortOrder: eff }
          })

          const docTreeChildren = withDocIdOnTree(transformToNested({
            data: dataForDoc,
            treeLevel: 1,
            posPrefix: `${masterDir[wallet].treePos}`,
            updateSortOrder: false,
          }), docId)

          updateSectionsBySectionIdSortOrderByTreeData(docTreeChildren, sectionsByWallet[wallet])
            .forEach(sId => changedSectionIdSet.add(sId))
        })

        const changedSectionIds = [...changedSectionIdSet]

        upsertDraftSectionEntries({
          draftSection: draftSectionsByWallet[wallet],
          sectionsBySectionId: sectionsByWallet[wallet],
          sectionIds: [...new Set([...changedSectionIds, sectionId])],
        })

        await props.handleWriteDraftSection()
        console.log('onDuplicateSection draftSectionsByWallet', draftSectionsByWallet)

        if (fieldsToUpdate?.length) {
          for (let idx = 0; idx < fieldsToUpdate.length; idx += 1) {
            const f = fieldsToUpdate[idx]
            const { scope, fieldId } = f

            // 因為 section.docs 被重置成 []，後端也會過濾 field.docs，所以需要在草稿加回跟 sourceSectionId 相同的 field.docs
            const updatedData = draftSectionsByWallet[wallet].updatedDataBySectionId[sectionId]
            if (!updatedData.fieldsData) updatedData.fieldsData = []
            updatedData.fieldsData.push(cloneDeep(f))

            if (fieldsByWallet[wallet][sourceSectionId]?.[scope]?.[fieldId]?.docs) {
              updatedData.fieldsData[idx].docs = cloneDeep(fieldsByWallet[wallet][sourceSectionId][scope][fieldId].docs)
            }

            if (!fieldsByWallet[wallet][sectionId]) fieldsByWallet[wallet][sectionId] = {}
            if (!originFieldsByWallet[wallet][sectionId]) originFieldsByWallet[wallet][sectionId] = {}

            if (!fieldsByWallet[wallet][sectionId][scope]) fieldsByWallet[wallet][sectionId][scope] = {}
            if (!originFieldsByWallet[wallet][sectionId][scope]) originFieldsByWallet[wallet][sectionId][scope] = {}

            fieldsByWallet[wallet][sectionId][scope][fieldId] = f
            originFieldsByWallet[wallet][sectionId][scope][fieldId] = cloneDeep(f)
          }
        }

        nextTick(() => {
          const el = document.getElementById(`${sectionData.docId}-${sectionId}`)
          if (el) el.click()
        })
      } else {
        antMessage.error(`Failed to duplicate section [${dataRef.enTitle} ${dataRef.cnTitle}]: ${message}`, 10)
      }
    }

    const handleCreateDoc = async (createDocData, batchCreateSectionsData) => {
      console.log('handleCreateDoc createDocData', { createDocData, batchCreateSectionsData })
      props.onChangeSpinVisible(true)

      let writeSectionsRes = {}
      const createDocRes = await createDoc(createDocData)
      const { data: docData, message } = createDocRes

      if (docData) {
        deriveDocDataWithTitlesAndTemplateData(docData, props.templateDataByTemplateId)
        emit('changeDocsDataByDocId', docData.docId, docData)

        writeSectionsRes = await batchWriteSections(batchCreateSectionsData)
        const { data: writeSectionsData, message: writeSectionsMsg } = writeSectionsRes

        if (writeSectionsData) {
          // emit('changeSectionsDataByDoc', docData.docId, writeSectionsData)
          writeSectionsData.forEach((s) => {
            const {
              sectionId, wallet, docs, customizedVersion,
            } = s

            if (sectionId === 'ReleaseVer') {
              releaseVerSectionByDocId[s.docId] = s
              return
            }

            if (customizedVersion) {
              if (!originSectionsByWallet[wallet][sectionId].customizedVersion) originSectionsByWallet[wallet][sectionId].customizedVersion = {}
              originSectionsByWallet[wallet][sectionId].customizedVersion = customizedVersion
              if (!sectionsByWallet[wallet][sectionId].customizedVersion) sectionsByWallet[wallet][sectionId].customizedVersion = {}
              sectionsByWallet[wallet][sectionId].customizedVersion = cloneDeep(customizedVersion)
            } else {
              delete originSectionsByWallet[wallet][sectionId].customizedVersion
              delete sectionsByWallet[wallet][sectionId].customizedVersion
            }

            originSectionsByWallet[wallet][sectionId].docs = docs
            sectionsByWallet[wallet][sectionId].docs = cloneDeep(docs)

            if (sectionId === props.selectedSectionState.sectionId && wallet === props.selectedSectionState.wallet) {
              props.onSelectSectionState(sectionsByWallet[wallet][sectionId])
            }
          })

          watchDocSectionsData(docData.docId)
          watchDocDataToUpdateTreeDocData(docData.docId)

          nextTick(() => {
            const el = document.getElementById(`${docData.docId}`)
            if (el) el.click()
          })

          antMessage.success(`Create doc [${docData.docShortTitle}] and its sections successfully`)
        } else {
          antMessage.error(`Failed to batch create sections: ${writeSectionsMsg}`, 10)
        }
      } else {
        antMessage.error(`Failed to create doc [${createDocData.docShortTitle}]: ${message}`, 10)
      }

      props.onChangeSpinVisible(false)
      return { createDocRes, writeSectionsRes }
    }

    expose({ treeData, treeNodes, handleCreateDoc })

    const handleDuplicateDoc = async ({ sourceDocId, ...duplicateDocData }) => {
      props.onChangeSpinVisible(true)
      const { data, message } = await duplicateDoc({ sourceDocId, ...duplicateDocData })

      if (data) {
        const { docData, sectionsToUpdate, fieldsToUpdate } = data
        const { docId, wallet } = docData

        deriveDocDataWithTitlesAndTemplateData(docData, props.templateDataByTemplateId)
        emit('changeDocsDataByDocId', docId, docData)

        if (sectionsToUpdate?.length) {
          sectionsToUpdate.forEach((s) => {
            const { sectionId, customizedVersion, docs } = s
            if (!customizedVersion) {
              delete originSectionsByWallet[wallet][sectionId].customizedVersion
              delete sectionsByWallet[wallet][sectionId].customizedVersion
            } else {
              originSectionsByWallet[wallet][sectionId].customizedVersion = customizedVersion
              sectionsByWallet[wallet][sectionId].customizedVersion = cloneDeep(customizedVersion)
            }

            originSectionsByWallet[wallet][sectionId].docs = docs
            sectionsByWallet[wallet][sectionId].docs = cloneDeep(docs)

            if (sectionId === props.selectedSectionState.sectionId && wallet === props.selectedSectionState.wallet) {
              props.onSelectSectionState(sectionsByWallet[wallet][sectionId])
            }
          })
        }

        if (fieldsToUpdate?.length) {
          fieldsToUpdate.forEach((f) => {
            const {
              sectionId, scope, fieldId, customizedVersion, docs,
            } = f

            if (!fieldsByWallet[wallet][sectionId]) fieldsByWallet[wallet][sectionId] = {}
            if (!originFieldsByWallet[wallet][sectionId]) originFieldsByWallet[wallet][sectionId] = {}

            if (!fieldsByWallet[wallet][sectionId][scope]) fieldsByWallet[wallet][sectionId][scope] = {}
            if (!originFieldsByWallet[wallet][sectionId][scope]) originFieldsByWallet[wallet][sectionId][scope] = {}

            if (!customizedVersion) {
              delete fieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion
              delete originFieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion
            } else {
              fieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion = customizedVersion
              originFieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion = cloneDeep(customizedVersion)
            }

            if (!docs) {
              delete fieldsByWallet[wallet][sectionId][scope][fieldId].docs
              delete originFieldsByWallet[wallet][sectionId][scope][fieldId].docs
            } else {
              fieldsByWallet[wallet][sectionId][scope][fieldId].docs = docs
              originFieldsByWallet[wallet][sectionId][scope][fieldId].docs = cloneDeep(docs)
            }
          })
        }

        console.log('handleDuplicateDoc', {
          sectionsDataByDocId: props.sectionsDataByDocId,
          originSectionsByWallet: originSectionsByWallet[wallet],
          sectionsByWallet: sectionsByWallet[wallet],
        })

        watchDocSectionsData(docId)
        watchDocDataToUpdateTreeDocData(docId)

        nextTick(() => {
          const el = document.getElementById(`${docId}`)
          if (el) el.click()
        })

        antMessage.success(`Duplicate doc [${docData.docTitle} (${docData.docShortTitle})] successfully`)
      } else {
        antMessage.error(`Failed to duplicate doc [${getDocTitle(duplicateDocData, props.templateDataByTemplateId)} (${getDocShortTitle(duplicateDocData, props.templateDataByTemplateId)})]: ${message}`, 10)
      }
      props.onChangeSpinVisible(false)
      return { data, message }
    }

    const duplicateDocState = reactive({
      route: '',
      findSameUrlDoc: null,
    })

    const duplicateDocRuleMsg = computed(() => {
      if (!duplicateDocState.route) return 'Please enter API doc route'
      if (!/^(?!.*master.*)[\w-]+$/.test(duplicateDocState.route)) return 'Invalid route format'
      if (duplicateDocState.findSameUrlDoc) return `This route has been used by "${duplicateDocState.findSameUrlDoc.docTitle} (${duplicateDocState.findSameUrlDoc.docShortTitle})"`
      return null
    })

    const onDuplicateDoc = async (dataRef) => {
      if (props.isDataChanged) {
        props.showSaveChangesModal()
        return
      }

      if (!isEmpty(props.draftSection.updatedDataBySectionId)) {
        props.showPublishDraftModal()
        return
      }

      duplicateDocState.templateId = dataRef.templateId
      duplicateDocState.wallet = dataRef.wallet
      if (dataRef.subName) duplicateDocState.subName = `Copy ${dataRef.subName}`

      AntModal.confirm({
        title: 'Please enter API doc title, sub name and route.',
        width: '500px',
        content: () => {
          const nodes = [
            h(
              'div',
              { style: { marginBottom: '8px' } },
              [
                h('label', { style: { marginBottom: '4px' } }, 'Title:'),
                h(AntInput, {
                  value: getDocTitle(duplicateDocState, props.templateDataByTemplateId),
                  disabled: true,
                }),
              ],
            ),
            h(
              'div',
              { style: { marginBottom: '8px' } },
              [
                h('label', { style: { marginBottom: '4px' } }, 'Subname (Optional):'),
                h(AntInput, {
                  value: duplicateDocState.subName,
                  placeholder: 'Subname',
                  'onUpdate:value': val => (duplicateDocState.subName = val),
                }),
              ],
            ),
            h(
              'div',
              { style: { display: 'flex', alignItems: 'center', marginTop: '8px' } },
              [
                h(
                  'div',
                  { style: { whiteSpace: 'nowrap', marginRight: '4px' } },
                  `${props.templateDataByTemplateId[dataRef.templateId]?.domain}/api-doc/${dataRef.wallet}-wallet/`,
                ),
                h(AntInput, {
                  value: duplicateDocState.route,
                  'onUpdate:value': val => (duplicateDocState.route = val),
                  onBlur: () => {
                    duplicateDocState.findSameUrlDoc = Object.values(props.docsDataByDocId).find(({ domain, wallet, route: docRoute }) => domain === dataRef.domain
                      && wallet === dataRef.wallet
                      && docRoute.toLowerCase() === duplicateDocState.route.toLowerCase())

                    console.log('findSameUrlDoc', duplicateDocState.findSameUrlDoc)
                  },
                  placeholder: 'route',
                }),
              ],
            ),
          ]

          if (duplicateDocRuleMsg.value) {
            nodes.push(
              h('div',
                { class: 'ant-form-item-explain-error' },
                duplicateDocRuleMsg.value),
            )
          }
          return h('div', {}, nodes)
        },
        okText: '確定',
        cancelText: '取消',
        onOk: async () => {
          if (!duplicateDocState.route || duplicateDocState.findSameUrlDoc) {
            throw new Error('Invalid route')
          }

          const host = dataRef.domain.replace(/^https?:\/\//, '')
          const docId = `${host}#${dataRef.wallet}#${duplicateDocState.route}`

          const duplicateDocData = {
            docId,
            currency: dataRef.currency,
            ...(dataRef.platforms && { platforms: dataRef.platforms }),
            ...(dataRef.excludedPlatforms && { excludedPlatforms: dataRef.excludedPlatforms }),
            domain: dataRef.domain,
            ...duplicateDocState,
          }

          releaseVerSectionByDocId[docId] = {
            ...cloneDeep(releaseVerSectionByDocId[dataRef.docId]),
            docId,
          }

          Object.entries(releaseVerFieldsByDocId[dataRef.docId] || {}).forEach(([scope, fields]) => {
            Object.entries(fields).forEach(([fieldId, field]) => {
              if (!releaseVerFieldsByDocId[docId]) releaseVerFieldsByDocId[docId] = {}
              if (!releaseVerFieldsByDocId[docId][scope]) releaseVerFieldsByDocId[docId][scope] = {}

              releaseVerFieldsByDocId[docId][scope][fieldId] = {
                ...cloneDeep(field),
                docId,
              }
            })
          })

          // const batchCreateSectionsData = []
          // props.sectionsDataByDocId[dataRef.docId].forEach(({ sectionId }) => {
          //   if (sectionId === 'ReleaseVer') return

          //   const originSectionInSectionsByWallet = originSectionsByWallet[wallet][sectionId]
          //   const sectionInSectionsByWallet = sectionsByWallet[wallet][sectionId]
          //   console.log('onDuplicateDoc sectionId', sectionId, { originSectionInSectionsByWallet, sectionInSectionsByWallet })

          //   originSectionInSectionsByWallet.docs = sortDocIds([...originSectionInSectionsByWallet.docs, docId])
          //   sectionInSectionsByWallet.docs = sortDocIds([...sectionInSectionsByWallet.docs, docId])

          //   if (originSectionInSectionsByWallet.customizedVersion?.[dataRef.docId]) {
          //     originSectionInSectionsByWallet.customizedVersion[docId] = cloneDeep(originSectionInSectionsByWallet.customizedVersion[dataRef.docId])
          //   }

          //   if (sectionInSectionsByWallet.customizedVersion?.[dataRef.docId]) {
          //     sectionInSectionsByWallet.customizedVersion[docId] = cloneDeep(sectionInSectionsByWallet.customizedVersion[dataRef.docId])
          //   }

          //   batchCreateSectionsData.push(originSectionInSectionsByWallet)
          // })

          // await nextTick() // 等待 props.sectionsDataByDocId[docId] 更新建立

          console.log('onDuplicateDoc', docId, {
            duplicateDocData,
            // sectionsDataByDocId: props.sectionsDataByDocId[docId],
            // originSectionsByWallet: originSectionsByWallet[wallet],
            // sectionsByWallet: sectionsByWallet[wallet],
            releaseVerSectionByDocId: releaseVerSectionByDocId[docId],
            releaseVerFieldsByDocId: releaseVerFieldsByDocId[docId],
            // batchCreateSectionsData,
          })

          return handleDuplicateDoc({ sourceDocId: dataRef.docId, ...duplicateDocData })
        },
      })
    }

    const onDuplicate = dataRef => (props.type === 'master' ? onDuplicateSection(dataRef) : onDuplicateDoc(dataRef))

    const peerWalletOf = wallet => (wallet === 'multi' ? 'single' : 'multi')

    const canMoveSectionToPeerWallet = (dataRef) => {
      const peerWallet = peerWalletOf(dataRef.wallet)
      const { parentSectionId } = dataRef
      if (!isRootParentSectionId(parentSectionId) && sectionsByWallet[peerWallet]?.[parentSectionId]) return true
      return false
    }

    /**
     * 將單一區塊移至另一 Wallet：改呼叫後端 move，並依 sectionsToUpdate / fieldsToUpdate 同步前端狀態。
     */
    const onMoveSectionToPeerWallet = async (dataRef) => {
      if (props.draftSection.updatedDataBySectionId?.[dataRef.sectionId]) {
        props.showPublishDraftModal()
        return
      }

      const sourceWallet = dataRef.wallet
      const peerWallet = peerWalletOf(sourceWallet)
      const targetDocId = `${peerWallet}#master`
      const { sectionId } = dataRef

      const normalizedPid = dataRef.parentSectionId
      const peerSections = Object.values(sectionsByWallet[peerWallet]).filter(
        s => s.parentSectionId === normalizedPid,
      )
      const sortOrder = peerSections.length

      const movedSection = cleanSection({
        ...dataRef,
        docId: targetDocId,
        wallet: peerWallet,
        docs: [],
        sortOrder,
      })
      delete movedSection.customizedVersion

      props.onChangeSpinVisible(true)
      try {
        const { data, message } = await moveSectionToPeerWallet({
          sourceDocId: dataRef.docId,
          ...movedSection,
        })
        if (!data) {
          antMessage.error(`Failed to move section: ${message || 'unknown error'}`, 10)
          return
        }

        const { sectionData, sectionsToUpdate = [], fieldsToUpdate = [] } = data

        if (draftSectionsByWallet[sourceWallet].updatedDataBySectionId?.[sectionId]) {
          delete draftSectionsByWallet[sourceWallet].updatedDataBySectionId[sectionId]
        }
        delete originSectionsByWallet[sourceWallet][sectionId]
        delete sectionsByWallet[sourceWallet][sectionId]
        delete originFieldsByWallet[sourceWallet][sectionId]
        delete fieldsByWallet[sourceWallet][sectionId]

        sectionsToUpdate.forEach((s) => {
          const { wallet, sectionId: sId } = s
          if (!wallet || !sId) return
          originSectionsByWallet[wallet][sId] = cloneDeep(s)
          sectionsByWallet[wallet][sId] = cloneDeep(s)
        })

        // sectionsToUpdate 僅含來源 wallet 同層重排；對側新 section 在 sectionData
        if (sectionData?.sectionId) {
          originSectionsByWallet[peerWallet][sectionId] = cloneDeep(sectionData)
          sectionsByWallet[peerWallet][sectionId] = cloneDeep(sectionData)
        }

        if (fieldsToUpdate?.length) {
          if (!fieldsByWallet[peerWallet][sectionId]) fieldsByWallet[peerWallet][sectionId] = {}
          if (!originFieldsByWallet[peerWallet][sectionId]) originFieldsByWallet[peerWallet][sectionId] = {}
          fieldsToUpdate.forEach((f) => {
            const { scope, fieldId } = f
            if (!fieldsByWallet[peerWallet][sectionId][scope]) fieldsByWallet[peerWallet][sectionId][scope] = {}
            if (!originFieldsByWallet[peerWallet][sectionId][scope]) originFieldsByWallet[peerWallet][sectionId][scope] = {}
            fieldsByWallet[peerWallet][sectionId][scope][fieldId] = cloneDeep(f)
            originFieldsByWallet[peerWallet][sectionId][scope][fieldId] = cloneDeep(f)
          })
        } else {
          fieldsByWallet[peerWallet][sectionId] = {}
          originFieldsByWallet[peerWallet][sectionId] = {}
        }

        const movedSectionState = sectionsByWallet[peerWallet][sectionId]
        if (movedSectionState) {
          if (props.activeTreeTab !== 'master') {
            emit('changeActiveTreeTab', 'master')
            await nextTick()
          }
          props.onSelectSectionState(movedSectionState)
          router.push(`/api-doc/editor/${peerWallet}/${sectionId}`)
          await nextTick()
          await expandRouteAndScrollToMasterSection(peerWallet)
        }

        antMessage.success(`已移至 ${titleCase(peerWallet)} Wallet`, 3)
      } finally {
        props.onChangeSpinVisible(false)
      }
    }

    const onDeleteSection = async (dataRef) => {
      console.log('onDeleteSection dataRef', dataRef)
      const deletedSectionIdxByDocIdMap = {}
      const modalDeleteSectionsData = [];

      // 使用 originSectionsByWallet[wallet][sectionId].docs 而非 dataRef.docs (sectionsByWallet[wallet][sectionId].docs)
      // 是因為 sectionsByWallet[wallet][sectionId].docs 可能被變動過還在草稿中
      (originSectionsByWallet[dataRef.wallet][dataRef.sectionId].docs || []).forEach((docId) => {
        const docSectionsData = props.sectionsDataByDocId[docId]
        docSectionsData.forEach(({
          sectionId, wallet, title,
        }, idx) => {
          if (sectionId === dataRef.sectionId && wallet === dataRef.wallet) {
            modalDeleteSectionsData.push({
              docId,
              sectionId,
              title,
              docShortTitle: docId.includes('master') ? `${capitalize(wallet)} Wallet` : props.docsDataByDocId[docId].docShortTitle,
            })

            deletedSectionIdxByDocIdMap[docId] = idx
          }
        })
      })

      const onOk = async () => {
        props.onChangeSpinVisible(true)
        const {
          docId, sectionId, parentSectionId, wallet, enTitle = '', cnTitle = '',
        } = dataRef

        const { data, message } = await deleteSection({ docId, sectionId })

        if (data) {
          const { sectionsToUpdate } = data
          antMessage.success(`Delete section [${enTitle} ${cnTitle}] successfully`)

          console.log('onDeleteSection draftSection', props.draftSection, 'selectedKeys', selectedKeys.value)

          delete originSectionsByWallet[wallet][sectionId]
          delete sectionsByWallet[wallet][sectionId]

          delete originFieldsByWallet[wallet][sectionId]
          delete fieldsByWallet[wallet][sectionId]

          // 刪除草稿中的該 section
          if (draftSectionsByWallet[wallet].updatedDataBySectionId?.[sectionId]) {
            delete draftSectionsByWallet[wallet].updatedDataBySectionId[sectionId]
            await props.handleWriteDraftSection()
          }

          if (sectionsToUpdate?.length) {
            sectionsToUpdate.forEach((s) => {
              const { sectionId: sId } = s
              sectionsByWallet[wallet][sId].sortOrder = s.sortOrder
              originSectionsByWallet[wallet][sId].sortOrder = s.sortOrder

              // 草稿若已存有該 section，需更新 sortOrder
              if (draftSectionsByWallet[wallet].updatedDataBySectionId?.[sId]?.sectionData) {
                draftSectionsByWallet[wallet].updatedDataBySectionId[sId].sectionData.sortOrder = s.sortOrder
              }
            })
          }

          // 已選擇要刪除的 section：改選父節點（不可 onCancel，資料已刪會清空 wallet）
          if (selectedKeys.value.includes(dataRef.treeKey)) {
            let jumpId = ''
            let jumpSectionData = null

            if (!isRootParentSectionId(parentSectionId)) {
              jumpId = `${docId}-${parentSectionId}`
              jumpSectionData = sectionsByWallet[wallet]?.[parentSectionId]
            } else if (!docId.includes('master')) {
              jumpId = docId
              jumpSectionData = props.docsDataByDocId?.[docId]
            } else {
              const walletDirIdx = wallet === 'multi' ? 0 : 1
              const firstChild = treeData.value[walletDirIdx]?.treeChildren?.[0]
              jumpId = firstChild?.treeKey
              if (firstChild?.sectionId) {
                jumpSectionData = sectionsByWallet[wallet]?.[firstChild.sectionId]
              }
            }

            if (jumpSectionData) {
              props.onSelectSectionState(jumpSectionData)
            }

            await nextTick()
            if (jumpId) {
              const el = document.getElementById(jumpId)
              if (el) {
                el.click()
                el.scrollIntoView({ block: 'nearest' })
              }
            }
          }
        } else {
          antMessage.error(`Failed to delete section [${enTitle} ${cnTitle}]: ${message}`)
        }
        props.onChangeSpinVisible(false)
      }

      if (modalDeleteSectionsData.length) {
        AntModal.confirm({
          class: 'delete-section-modal',
          title: '此刪除動作不會經過子本的發佈版本流程，刪除後將無法復原，確認是否要執行？(如需發佈版本，請從 Used In 欄位中移除子本)',
          width: '600px',
          content() {
            const nodes = modalDeleteSectionsData.map(({ docId: doc, docShortTitle, title }) => [
              h(
                'div',
                { style: { marginBottom: '8px' } },
                [
                  h(AntTag,
                    { color: doc.includes('master') ? 'orange' : 'blue' },
                    doc.includes('master') ? '母本' : '子本'),
                  h(
                    'span',
                    docShortTitle,
                  ),
                  h(
                    'div',
                    { style: { color: '#1890ff', marginLeft: '48px' } },
                    title,
                  ),
                ],
              ),
            ])
            return h('div', {}, nodes)
          },
          okText: '確定',
          cancelText: '取消',
          onOk,
        })
      } else {
        AntModal.confirm({
          title: `刪除後將無法復原，確認刪除「${dataRef.treeTitleWithRemark}」？`,
          okText: 'Confirm',
          onOk,
        })
      }
    }

    const onDeleteDoc = async (dataRef) => {
      if (props.isDataChanged) {
        props.showSaveChangesModal()
        return
      }

      if (!isEmpty(props.draftSection.updatedDataBySectionId)) {
        props.showPublishDraftModal()
        return
      }

      const { docShortTitle } = dataRef

      AntModal.confirm({
        title: `刪除後將無法復原，確認刪除 ${dataRef.docTitle} (${docShortTitle})？`,
        okText: 'Confirm',
        onOk: async () => {
          props.onChangeSpinVisible(true)

          const { data, message } = await deleteDoc(dataRef.docId)

          if (data) {
            const { sectionsToUpdate, fieldsToUpdate } = data
            console.log('onDeleteDoc', { sectionsToUpdate, fieldsToUpdate })
            emit('changeDocsDataByDocId', dataRef.docId, undefined)

            if (sectionsToUpdate?.length) {
              sectionsToUpdate.forEach((s) => {
                const {
                  wallet, sectionId, customizedVersion, docs,
                } = s
                if (!customizedVersion) {
                  delete sectionsByWallet[wallet][sectionId].customizedVersion
                  delete originSectionsByWallet[wallet][sectionId].customizedVersion
                } else {
                  if (!sectionsByWallet[wallet][sectionId].customizedVersion) sectionsByWallet[wallet][sectionId].customizedVersion = {}
                  sectionsByWallet[wallet][sectionId].customizedVersion = cloneDeep(customizedVersion)
                  if (!originSectionsByWallet[wallet][sectionId].customizedVersion) originSectionsByWallet[wallet][sectionId].customizedVersion = {}
                  originSectionsByWallet[wallet][sectionId].customizedVersion = cloneDeep(customizedVersion)
                }

                if (!sectionsByWallet[wallet][sectionId].docs) sectionsByWallet[wallet][sectionId].docs = []
                sectionsByWallet[wallet][sectionId].docs = docs
                if (!originSectionsByWallet[wallet][sectionId].docs) originSectionsByWallet[wallet][sectionId].docs = []
                originSectionsByWallet[wallet][sectionId].docs = cloneDeep(docs)

                if (sectionId === props.selectedSectionState.sectionId && wallet === props.selectedSectionState.wallet) {
                  props.onSelectSectionState(sectionsByWallet[wallet][sectionId])
                }
              })
            }

            if (fieldsToUpdate?.length) {
              fieldsToUpdate.forEach((f) => {
                const {
                  wallet, sectionId, scope, fieldId, customizedVersion, docs,
                } = f
                if (!fieldsByWallet[wallet][sectionId]) fieldsByWallet[wallet][sectionId] = {}
                if (!originFieldsByWallet[wallet][sectionId]) originFieldsByWallet[wallet][sectionId] = {}

                if (!fieldsByWallet[wallet][sectionId][scope]) fieldsByWallet[wallet][sectionId][scope] = {}
                if (!originFieldsByWallet[wallet][sectionId][scope]) originFieldsByWallet[wallet][sectionId][scope] = {}

                if (!customizedVersion) {
                  delete fieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion
                  delete originFieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion
                } else {
                  if (!fieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion) fieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion = {}
                  fieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion = cloneDeep(customizedVersion)
                  if (!originFieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion) originFieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion = {}
                  originFieldsByWallet[wallet][sectionId][scope][fieldId].customizedVersion = cloneDeep(customizedVersion)
                }

                if (!docs) {
                  delete fieldsByWallet[wallet][sectionId][scope][fieldId].docs
                  delete originFieldsByWallet[wallet][sectionId][scope][fieldId].docs
                } else {
                  fieldsByWallet[wallet][sectionId][scope][fieldId].docs = docs
                  originFieldsByWallet[wallet][sectionId][scope][fieldId].docs = cloneDeep(docs)
                }
              })
            }

            if (props.selectedSectionState.docId === dataRef.docId) { // 已選擇要刪除的 doc，又刪除該 doc，則跳轉到同 wallet 的第一個 doc
              const walletDirIdx = docDir[dataRef.wallet].treePos
              const jumpToDocId = displayedTreeData.value[walletDirIdx].treeChildren[0]?.treeKey
              if (jumpToDocId) {
                document.getElementById(`${jumpToDocId}`).click()
                document.getElementById(`${jumpToDocId}`).scrollIntoView()
              }
            }
            antMessage.success(`Delete doc [${docShortTitle}] successfully`)
          } else {
            antMessage.error(`Failed to delete doc [${docShortTitle}]: ${message}`)
          }
          props.onChangeSpinVisible(false)
        },
      })
    }

    const onDelete = dataRef => (props.type === 'master' ? onDeleteSection(dataRef) : onDeleteDoc(dataRef))

    const resolveTreeDropWallet = (sectionRef = {}) => {
      const { wallet, docId, treeKey } = sectionRef
      if (wallet === 'multi' || wallet === 'single') return wallet
      const hint = String(docId || treeKey || '')
      if (hint.includes('single')) return 'single'
      if (hint.includes('multi')) return 'multi'
      return null
    }

    const getAntTreeNodeKey = (node) => {
      if (!node) return ''
      return node.eventKey ?? node.key ?? node.treeKey ?? ''
    }

    /** treeKey 格式：${docId}-${sectionId}（例：multi#master-R4apOBLVZi） */
    const parseTreeKeyToDocAndSection = (treeKey) => {
      const s = String(treeKey || '')
      if (!s) return null
      const dash = s.lastIndexOf('-')
      if (dash <= 0) return null
      const sectionId = s.slice(dash + 1)
      const docId = s.slice(0, dash)
      if (!sectionId || !docId) return null
      return { docId, sectionId, treeKey: s }
    }

    /** Ant drop 節點的 dataRef 可能缺欄位；先從 treeKey 還原 sectionId，再以 sectionsByWallet 補齊 */
    const enrichTreeDropRef = (rawRef = {}, antNode = null) => {
      let sectionRef = { ...(rawRef || {}) }
      const treeKey = sectionRef.treeKey || sectionRef.eventKey || getAntTreeNodeKey(antNode)
      if (!sectionRef.sectionId && treeKey) {
        const parsed = parseTreeKeyToDocAndSection(treeKey)
        if (parsed) sectionRef = { ...parsed, ...sectionRef, treeKey }
      }
      if (!sectionRef.sectionId) return sectionRef

      const wallet = resolveTreeDropWallet(sectionRef)
      const stored = wallet ? sectionsByWallet[wallet]?.[sectionRef.sectionId] : null
      if (!stored) {
        return { ...sectionRef, ...(wallet ? { wallet } : {}) }
      }
      return {
        ...stored,
        ...sectionRef,
        wallet: wallet || stored.wallet || sectionRef.wallet,
        parentSectionId: stored.parentSectionId ?? sectionRef.parentSectionId,
        docId: stored.docId ?? sectionRef.docId,
        sectionType: stored.sectionType ?? sectionRef.sectionType,
      }
    }

    const getWalletTopSectionListForTreeDrop = (wallet, docId) => {
      const normalizedWallet = resolveTreeDropWallet({ wallet, docId })
      if (!normalizedWallet) return []
      const root = treeData.value.find(d => d.treeKey === `${normalizedWallet}#${props.type}`)
      if (!root?.treeChildren) return []
      if (props.type === 'doc') {
        const docNode = root.treeChildren.find(d => d.treeKey === docId)
        return docNode?.treeChildren || []
      }
      return root.treeChildren
    }

    const findSectionNodeDeepForTreeDrop = (nodes, sectionId) => {
      if (!nodes?.length) return null
      for (const n of nodes) {
        if (n.sectionId === sectionId) return n
        const inner = findSectionNodeDeepForTreeDrop(n.treeChildren, sectionId)
        if (inner) return inner
      }
      return null
    }

    const getChildrenRowForParentForTreeDrop = (wallet, docId, parentPid) => {
      if (isRootParentSectionId(parentPid)) {
        return getWalletTopSectionListForTreeDrop(wallet, docId)
      }
      const root = treeData.value.find(d => d.treeKey === `${wallet}#${props.type}`)
      if (!root?.treeChildren?.length) return []
      if (props.type === 'master') {
        const pNode = findSectionNodeDeepForTreeDrop(root.treeChildren, parentPid)
        return pNode?.treeChildren || []
      }
      const docNode = root.treeChildren.find(d => d.treeKey === docId)
      if (!docNode?.treeChildren?.length) return []
      const pNode = findSectionNodeDeepForTreeDrop(docNode.treeChildren, parentPid)
      return pNode?.treeChildren || []
    }

    /** Ant Tree 事件節點有 pos；treeData 節點來自 transformToNested 則為 treePos */
    const getTreeNodePosStr = (node) => {
      if (!node) return ''
      if (node.pos != null && node.pos !== '') return String(node.pos)
      if (node.treePos != null && node.treePos !== '') return String(node.treePos)
      return ''
    }

    const isSameParentForTreeReorder = (dragRef, dropRef) => (
      dragRef.docId === dropRef.docId
      && dragRef.sectionId !== dropRef.sectionId
      && (
        dragRef.parentSectionId === dropRef.parentSectionId
        || (
          isRootParentSectionId(dragRef.parentSectionId)
          && isRootParentSectionId(dropRef.parentSectionId)
        )
      )
    )

    const calcEffDropPositionFromNode = (node, infoDropPosition) => {
      const posStr = getTreeNodePosStr(node)
      const dropPosArr = posStr.split('-').filter(Boolean)
      const lastSeg = Number(dropPosArr[dropPosArr.length - 1]) || 0
      return infoDropPosition - lastSeg
    }

    /**
     * Ant Tree 常把同層重排誤報成 dropToGap: false（拖到節點本體）：
     * 1) drop 在父節點 → 改為插到前一兄弟之前的縫隙
     * 2) drop 在同層兄弟 → 改為同層縫隙（上移 -1、下移 +1）
     */
    const resolveTreeDropForReorder = (info, dragRef, dropNode, dropRef) => {
      let effDropNode = dropNode
      let effDropRef = dropRef
      let effDropToGap = info.dropToGap
      let usedSyntheticReorder = false
      let effDropPosition

      if (!info.dropToGap && dropRef.sectionId === dragRef.parentSectionId && dragRef.sectionId !== dropRef.sectionId) {
        const dragParent = dragRef.parentSectionId
        if (!isRootParentSectionId(dragParent)) {
          const childRow = getChildrenRowForParentForTreeDrop(dragRef.wallet, dragRef.docId, dragParent)
          const dragIdx = childRow.findIndex(n => n.sectionId === dragRef.sectionId)
          if (dragIdx > 0) {
            const prev = childRow[dragIdx - 1]
            if (prev) {
              effDropNode = prev
              effDropRef = prev.dataRef || prev
              effDropToGap = true
              usedSyntheticReorder = true
              effDropPosition = -1
            }
          }
        }
      } else if (!info.dropToGap) {
        const childRow = getChildrenRowForParentForTreeDrop(
          dragRef.wallet,
          dragRef.docId,
          dragRef.parentSectionId,
        )
        let dropIdx = dropRef.sectionId
          ? childRow.findIndex(n => n.sectionId === dropRef.sectionId)
          : -1
        if (dropIdx < 0 && dropNode) {
          const posArr = getTreeNodePosStr(dropNode).split('-').filter(Boolean)
          const idxInRow = Number(posArr[posArr.length - 1])
          if (!Number.isNaN(idxInRow) && childRow[idxInRow]) dropIdx = idxInRow
        }
        const dragIdx = childRow.findIndex(n => n.sectionId === dragRef.sectionId)
        const dropSectionId = dropRef.sectionId || childRow[dropIdx]?.sectionId
        const sameParentSibling = dragIdx > -1 && dropIdx > -1
          && dragRef.sectionId !== dropSectionId
        if (sameParentSibling) {
          const dropRowNode = childRow[dropIdx]
          effDropNode = dropRowNode
          effDropRef = dropRowNode.dataRef || dropRowNode
          effDropToGap = true
          usedSyntheticReorder = true
          effDropPosition = dragIdx > dropIdx ? -1 : 1
        }
      }

      if (effDropPosition === undefined) {
        effDropPosition = calcEffDropPositionFromNode(effDropNode, info.dropPosition)
      }

      return {
        effDropNode,
        effDropRef,
        effDropToGap,
        effDropPosition,
        usedSyntheticReorder,
      }
    }

    /** 僅用於 onDrop：集中驗證 */
    const validateSectionTreeDrop = (info, dragNode, dropNode, dragRef, dropRef) => {
      const dragRefE = enrichTreeDropRef(dragRef, dragNode)
      const dropRefE = enrichTreeDropRef(dropRef, dropNode)

      const normSegs = pos => (pos || '').split('-').slice(1).join('-').split('-')
        .filter(Boolean)

      const resolved = resolveTreeDropForReorder(info, dragRefE, dropNode, dropRefE)
      const {
        effDropNode, effDropRef: effDropRefRaw, effDropToGap, effDropPosition, usedSyntheticReorder,
      } = resolved
      const effDropRef = enrichTreeDropRef(effDropRefRaw, effDropNode)

      const validationParentPid = (() => {
        if (usedSyntheticReorder && dragRefE.sectionId) return dragRefE.parentSectionId
        if (effDropRef.sectionId && isSameParentForTreeReorder(dragRefE, effDropRef)) {
          return dragRefE.parentSectionId
        }
        if (effDropRef.sectionId) return effDropRef.parentSectionId
        return dragRefE.parentSectionId
      })()

      const effectiveDropParentSectionIdEarly = validationParentPid

      const fail = reason => ({
        ok: false,
        reason,
        effectiveDropParentSectionId: effectiveDropParentSectionIdEarly,
      })

      const dropWallet = resolveTreeDropWallet(effDropRef)
      const dragWallet = resolveTreeDropWallet(dragRefE)
      const crossWallet = Boolean(dragWallet && dropWallet && dragWallet !== dropWallet)
      if (crossWallet) {
        return fail('不支援跨 Multi / Single 錢包拖曳，請在同一側調整順序')
      }

      // 1. 僅允許同層縫隙；Ant 有時誤報 dropToGap，見 resolveTreeDropForReorder
      if (!effDropToGap) {
        return fail('請拖到節點之間的縫隙以調整同層順序（不支援拖到節點上變成子節點）')
      }
      const dropPosition = effDropPosition
      const nestAsChildGap = (effDropNode.treeChildren || []).length > 0 && effDropNode.expanded && dropPosition === 1
      if (nestAsChildGap) {
        return fail('此位置會把區塊插入為子節點，僅允許同層排序；請收合父節點或改拖到同層縫隙')
      }

      // 誤判 dropToGap 而改為同層縫隙時，Ant 的 dragNode.pos 與 treeData 的 treePos 分段常不一致，不可再用長度比層級
      if (!usedSyntheticReorder) {
        const da = normSegs(getTreeNodePosStr(dragNode))
        const db = normSegs(getTreeNodePosStr(effDropNode))
        if (da.length !== db.length) {
          return fail('拖曳與放置目標層級不同，僅能在同一層級移動')
        }
      }

      const dragPid = dragRefE.parentSectionId
      const dropPid = validationParentPid

      // 2. 同 wallet 下可跨父節點同層移動：依「放置側父節點」的 sectionType 檢查子區塊類型；ROOT 維持 info → apis → appendices 順序。

      /** 與新增子區一致：apis → api、appendices → appendix、info → info、appendix 下可再掛 appendix */
      const allowedUnderParentSectionType = (parentType, dragType) => {
        if (parentType === 'apis') return dragType === 'api'
        if (parentType === 'appendices') return dragType === 'appendix'
        if (parentType === 'info') return dragType === 'info'
        if (parentType === 'appendix') return dragType === 'appendix'
        return false
      }

      /** ROOT 頂層列：附錄區為 appendices；頂層若出現 appendix 一併視為附錄區（異常資料相容） */
      const isAppendixTopType = t => t === 'appendix' || t === 'appendices'
      /** 頂層 APIs 區： pillar 為 apis；同層多個 API 區塊可能為 api */
      const isApisTopType = t => t === 'apis' || t === 'api'

      if (dragRefE.docId !== effDropRef.docId) {
        return fail('不可跨子本拖曳區塊')
      }

      if (isRootParentSectionId(dropPid)) {
        if (!effDropRef.sectionId) {
          return fail('無法辨識放置的區塊，請拖到目標節點的上下縫隙')
        }
        const dropDocId = effDropRef.docId || dragRefE.docId
        const dropList = getWalletTopSectionListForTreeDrop(dropWallet, dropDocId)
        const dragList = getWalletTopSectionListForTreeDrop(dragWallet, dragRefE.docId)
        if (!dropList.length) return fail('找不到目標 wallet 的頂層區塊列表')

        const firstInfoIdx = dropList.findIndex(d => d.sectionType === 'info' && d.sectionId !== 'ReleaseVer')
        const lastInfoIdx = dropList.findLastIndex(d => d.sectionType === 'info' && d.sectionId !== 'ReleaseVer')
        const firstApisIdx = dropList.findIndex(d => isApisTopType(d.sectionType))
        const lastApisIdx = dropList.findLastIndex(d => isApisTopType(d.sectionType))
        const firstAppendixIdx = dropList.findIndex(d => isAppendixTopType(d.sectionType))
        const lastAppendixIdx = dropList.findLastIndex(d => isAppendixTopType(d.sectionType))

        const dragIdx = dragList.findIndex(n => n.sectionId === dragRefE.sectionId)
        const dropIdx = dropList.findIndex(n => n.sectionId === effDropRef.sectionId)
        if (dropIdx < 0) return fail('無法在樹狀資料中定位放置的頂層區塊')

        const sameRootParent = isRootParentSectionId(dragPid)
        if (sameRootParent && dragIdx < 0) {
          return fail('無法在樹狀資料中定位拖曳的頂層區塊')
        }

        const dropGapInsertIdx = dropPosition === -1 ? dropIdx : dropIdx + 1

        const st = dragRefE.sectionType
        const inInfoZone = idx => firstInfoIdx >= 0 && lastInfoIdx >= 0 && idx >= firstInfoIdx && idx <= lastInfoIdx
        const inApisZone = idx => firstApisIdx >= 0 && lastApisIdx >= 0 && idx >= firstApisIdx && idx <= lastApisIdx
        const inAppendixZone = idx => firstAppendixIdx >= 0 && lastAppendixIdx >= 0 && idx >= firstAppendixIdx && idx <= lastAppendixIdx

        if (st === 'info') {
          if (sameRootParent && !inInfoZone(dragIdx)) {
            return fail('Info 區塊僅能置於最前')
          }
          // 允許插入點為「最後一個 Info 節點之後的縫隙」（lastInfoIdx + 1），
          // 例如僅有兩個 Info: idx = 1,2，拖曳 1 到 2 之下時 dropGapInsertIdx = 3，仍屬 Info 區塊。
          const gapOk = dropGapInsertIdx >= firstInfoIdx && dropGapInsertIdx <= lastInfoIdx + 1
          if (!gapOk) {
            return fail('Info 區塊僅能置於最前')
          }
        } else if (isApisTopType(st)) {
          if (sameRootParent && !inApisZone(dragIdx)) {
            return fail('API 區塊僅能置於中間')
          }
          const apisCorridorEnd = firstAppendixIdx >= 0 ? firstAppendixIdx : dropList.length
          const gapOk = firstApisIdx >= 0 && lastApisIdx >= 0
            && dropGapInsertIdx >= firstApisIdx
            && dropGapInsertIdx <= apisCorridorEnd
          if (!gapOk) {
            return fail('API 區塊僅能置於中間')
          }
        } else if (isAppendixTopType(st)) {
          if (sameRootParent && !inAppendixZone(dragIdx)) {
            return fail('Appendix 僅能置於最末')
          }
          const gapOk = firstAppendixIdx >= 0 && lastAppendixIdx >= 0
            && dropGapInsertIdx >= firstAppendixIdx
            && dropGapInsertIdx <= dropList.length
          if (!gapOk) {
            return fail('Appendix 僅能置於最末')
          }
        }
      } else {
        const parentSec = sectionsByWallet[dropWallet]?.[dropPid]
        if (!parentSec) {
          return fail('找不到放置側父節點')
        }
        if (!allowedUnderParentSectionType(parentSec.sectionType, dragRefE.sectionType)) {
          return fail('此區塊類型無法置於選定的父節點底下')
        }
        const childRow = getChildrenRowForParentForTreeDrop(
          dropWallet,
          effDropRef.docId || dragRefE.docId,
          dropPid,
        )
        const dropIdx = childRow.findIndex(n => n.sectionId === effDropRef.sectionId)
        if (dropIdx < 0) {
          return fail('無法在樹狀資料中定位放置節點')
        }
        const dropGapInsertIdx = dropPosition === -1 ? dropIdx : dropIdx + 1
        if (dropGapInsertIdx < 0 || dropGapInsertIdx > childRow.length) {
          return fail('無效的插入位置')
        }
      }

      return {
        ok: true,
        effectiveDropParentSectionId: effectiveDropParentSectionIdEarly,
        resolved: {
          ...resolved,
          effDropRef,
        },
      }
    }

    /**
     * 樹狀拖曳重排：同層縫隙插入；可跨父節點（依父 sectionType 檢查），成功後更新 parentSectionId。
     */
    const onDrop = async (info) => {
      const notifyDropRejected = (reason, detail = {}) => {
        antMessage.warning(reason, 5)
        console.warn('[SectionTree onDrop]', reason, detail)
      }
      const debugDocId = 'allwecan.info#multi#PRD'
      const debugSectionIds = ['LFj2SGxLI4', 'R4apOBLVZi']
      const logDebugSortOrders = (stage, extra = {}) => {
        const rows = debugSectionIds.map((sid) => {
          const section = sectionsByWallet.multi?.[sid]
          const cv = section?.customizedVersion?.[debugDocId]
          return {
            stage,
            sectionId: sid,
            parentSectionId: section?.parentSectionId,
            masterSortOrder: section?.sortOrder,
            customizedSortOrder: cv?.sortOrder,
            hasCustomizedVersion: !!cv,
          }
        })
        console.groupCollapsed(`[SectionTree][debug-sort] ${stage}`)
        if (Object.keys(extra).length) console.log('extra', extra)
        console.table(rows)
        console.groupEnd()
      }

      const { dragNode } = info
      const dropNode = info.node
      const dragRef = enrichTreeDropRef(dragNode.dataRef || dragNode, dragNode)
      const dropRef = enrichTreeDropRef(dropNode.dataRef || dropNode, dropNode)
      logDebugSortOrders('before-validate', {
        dragSectionId: dragRef.sectionId,
        dropSectionId: dropRef.sectionId,
        dragDocId: dragRef.docId,
        dropDocId: dropRef.docId,
        dropToGap: info.dropToGap,
        rawDropPosition: info.dropPosition,
      })

      if (dragRef.sectionId === 'ReleaseVer') {
        notifyDropRejected('Release Version 區塊不可拖曳', { sectionId: dragRef.sectionId })
        return
      }
      if (props.draftSection.wallet !== dragRef.wallet && !isEmpty(props.draftSection.updatedDataBySectionId)) {
        notifyDropRejected(`請先發佈當前 ${titleCase(props.draftSection.wallet)} Wallet 草稿`)
        return
      }

      const validation = validateSectionTreeDrop(info, dragNode, dropNode, dragRef, dropRef)
      if (!validation.ok) {
        logDebugSortOrders('validation-rejected', {
          reason: validation.reason,
          dragSectionId: dragRef.sectionId,
          dropSectionId: dropRef.sectionId,
          dragDocId: dragRef.docId,
          dropDocId: dropRef.docId,
        })
        notifyDropRejected(validation.reason || '無法完成拖曳', {
          dropToGap: info.dropToGap,
          resolvedDropToGap: validation.resolved?.effDropToGap,
          drag: {
            sectionId: dragRef.sectionId, wallet: dragRef.wallet, sectionType: dragRef.sectionType, parentSectionId: dragRef.parentSectionId,
          },
          drop: {
            sectionId: dropRef.sectionId, wallet: dropRef.wallet, sectionType: dropRef.sectionType, parentSectionId: dropRef.parentSectionId,
          },
        })
        return
      }

      const { effDropNode, effDropRef, effDropPosition } = validation.resolved

      props.onChangeSpinVisible(true)

      const dropKey = effDropNode.eventKey ?? effDropNode.treeKey
      const dragKey = dragNode.eventKey ?? dragNode.treeKey
      let dropPosition = effDropPosition
      const sameParentSameDoc = dragRef.docId === effDropRef.docId
        && (
          dragRef.parentSectionId === effDropRef.parentSectionId
          || (
            isRootParentSectionId(dragRef.parentSectionId)
            && isRootParentSectionId(effDropRef.parentSectionId)
          )
        )
      const siblingRowBeforeDrop = sameParentSameDoc
        ? getChildrenRowForParentForTreeDrop(
          dragRef.wallet,
          dragRef.docId,
          dragRef.parentSectionId,
        )
        : []
      const siblingOrderBeforeDrop = siblingRowBeforeDrop.map(n => n.sectionId).filter(Boolean)

      // 同父同層拖曳時，以原同層索引強制校正插入方向，避免「拖到下方縫隙卻仍維持原順序」。
      if (sameParentSameDoc) {
        const dragIdxInRow = siblingRowBeforeDrop.findIndex(n => n.sectionId === dragRef.sectionId)
        const dropIdxInRow = siblingRowBeforeDrop.findIndex(n => n.sectionId === effDropRef.sectionId)
        if (dragIdxInRow > -1 && dropIdxInRow > -1) {
          if (dragIdxInRow < dropIdxInRow) dropPosition = 1
          if (dragIdxInRow > dropIdxInRow) dropPosition = -1
        }
      }

      const loop = (data, key, callback) => {
        data.forEach((item, index, arr) => {
          if (item.treeKey === key) return callback(item, index, arr)
          if (item.treeChildren) return loop(item.treeChildren, key, callback)
          return undefined
        })
      }

      const data = cloneDeep(treeData.value)
      let dragObj = {}
      let foundDragSource = false
      loop(data, dragKey, (item, index, arr) => {
        foundDragSource = true
        arr.splice(index, 1)
        dragObj = item
      })

      if (!foundDragSource || !dragObj.sectionId) {
        notifyDropRejected('在樹狀資料中找不到拖曳節點', { dragKey })
        props.onChangeSpinVisible(false)
        return
      }

      let targetArr = []
      let insertIndex = 0
      let foundDropTarget = false
      loop(data, dropKey, (item, index, arr) => {
        foundDropTarget = true
        targetArr = arr
        insertIndex = index
      })

      if (!foundDropTarget) {
        notifyDropRejected('在樹狀資料中找不到放置目標', { dropKey })
        props.onChangeSpinVisible(false)
        return
      }

      if (dropPosition === -1) targetArr.splice(insertIndex, 0, dragObj)
      else targetArr.splice(insertIndex + 1, 0, dragObj)

      // 保底：若 splice 後同層順序仍未改變，強制依 drag/drop 方向重排一次，避免 no-op。
      if (sameParentSameDoc && siblingOrderBeforeDrop.length > 1) {
        const siblingOrderAfterSplice = targetArr.map(n => n.sectionId).filter(Boolean)
        const unchangedOrder = siblingOrderAfterSplice.length === siblingOrderBeforeDrop.length
          && siblingOrderAfterSplice.every((sid, idx) => sid === siblingOrderBeforeDrop[idx])
        if (unchangedOrder) {
          const dragIdx = targetArr.findIndex(n => n.sectionId === dragRef.sectionId)
          const dropIdx = targetArr.findIndex(n => n.sectionId === effDropRef.sectionId)
          if (dragIdx > -1 && dropIdx > -1 && dragIdx !== dropIdx) {
            const [dragItem] = targetArr.splice(dragIdx, 1)
            const adjustedDropIdx = targetArr.findIndex(n => n.sectionId === effDropRef.sectionId)
            const insertAt = dropPosition === -1 ? adjustedDropIdx : adjustedDropIdx + 1
            targetArr.splice(insertAt, 0, dragItem)
          }
        }
      }

      const newParentSectionId = isRootParentSectionId(effDropRef.parentSectionId)
        ? PARENT_SECTION_ROOT
        : effDropRef.parentSectionId
      dragObj.parentSectionId = newParentSectionId
      const dw = dragObj.wallet
      const dsid = dragObj.sectionId
      if (sectionsByWallet[dw]?.[dsid]) {
        sectionsByWallet[dw][dsid].parentSectionId = newParentSectionId
      }

      treeData.value = data
      displayedTreeData.value = data

      const dragDocId = dragObj.docId
      const sortScopeParentId = isRootParentSectionId(newParentSectionId)
        ? PARENT_SECTION_ROOT
        : newParentSectionId

      const changedPairs = []
      for (const w of ['multi', 'single']) {
        const walletRoot = treeData.value.find(d => d.treeKey === `${w}#${props.type}`)
        if (!walletRoot?.treeChildren) continue
        if (props.type === 'master') {
          const masterSortChangedIds = updateSectionsBySectionIdSortOrderByTreeData(
            walletRoot.treeChildren,
            sectionsByWallet[w],
          )
          masterSortChangedIds.forEach((sId) => { changedPairs.push({ w, sId }) })
          syncTreeNodesSortOrderFromSections(walletRoot.treeChildren, sectionsByWallet[w], `${w}#master`)
          // 僅清除「母本 sortOrder 有變」的 section 在子本上的 customized sortOrder
          stripChildCustomizedSortOrdersForSectionIds(masterSortChangedIds, sectionsByWallet[w])
            .forEach((sId) => { changedPairs.push({ w, sId }) })
          const docWalletRoot = treeData.value.find(d => d.treeKey === `${w}#doc`)
          if (docWalletRoot) {
            rebuildWalletDocSectionTrees({
              walletRoot: docWalletRoot,
              sectionsBySectionId: sectionsByWallet[w],
              releaseVerSectionByDocId: props.releaseVerSectionByDocId,
            })
          }
        }
      }

      if (props.type === 'doc' && dragDocId && !dragDocId.includes('master')) {
        const dragWallet = dragObj.wallet
        const docWalletRoot = treeData.value.find(d => d.treeKey === `${dragWallet}#doc`)
        const targetDocNode = docWalletRoot?.treeChildren?.find(
          node => (node.docId || node.treeKey) === dragDocId,
        )

        if (targetDocNode?.treeChildren?.length) {
          applyDocSortOrdersFromTreeChildren(
            targetDocNode.treeChildren,
            dragDocId,
            sectionsByWallet[dragWallet],
            { scopeParentSectionId: sortScopeParentId },
          ).forEach((sId) => { changedPairs.push({ w: dragWallet, sId }) })

          const preserved = rebuildDocSectionTreeChildrenPreservingOrder({
            treeChildren: targetDocNode.treeChildren,
            sectionsBySectionId: sectionsByWallet[dragWallet],
            docId: dragDocId,
            treeLevel: (targetDocNode.treeLevel ?? 1) + 1,
            posPrefix: targetDocNode.treePos,
          })
          const releaseVer = getReleaseVerSectionForDoc(dragDocId)
          const hasReleaseVerNode = preserved[0]?.sectionId === 'ReleaseVer'
          targetDocNode.treeChildren = hasReleaseVerNode || !releaseVer
            ? preserved
            : buildDocSectionTreeChildrenForDoc({
              docId: dragDocId,
              sectionsBySectionId: sectionsByWallet[dragWallet],
              releaseVerSection: releaseVer,
              treeLevel: (targetDocNode.treeLevel ?? 1) + 1,
              posPrefix: targetDocNode.treePos,
              updateSortOrder: false,
            })
          syncTreeNodesSortOrderFromSections(
            targetDocNode.treeChildren,
            sectionsByWallet[dragWallet],
            dragDocId,
          )
        }
      }

      const seen = new Set()
      const changedSectionEntries = changedPairs.filter(({ w, sId }) => {
        const k = `${w}:${sId}`
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })

      const movedW = dragObj.wallet
      const movedSid = dragObj.sectionId
      if (!changedSectionEntries.some(({ w, sId }) => w === movedW && sId === movedSid)) {
        changedSectionEntries.push({ w: movedW, sId: movedSid })
      }

      if (changedSectionEntries.length) {
        const entriesByWallet = changedSectionEntries.reduce((acc, { w, sId }) => {
          if (!acc[w]) acc[w] = []
          acc[w].push(sId)
          return acc
        }, {})
        Object.entries(entriesByWallet).forEach(([w, sIds]) => {
          const dedupedIds = [...new Set(sIds)]
          // 母本拖曳後，除了本次 sortOrder 變更 section，還需同步修正草稿中既有但已與 sectionsByWallet 不一致的條目
          // （例如先拖曳再拖回原位，部分 section 可能未再次進入 changedPairs，導致 draft 殘留舊 sectionData）
          const sectionIdsToUpsert = props.type === 'master'
            ? [
              ...new Set([
                ...dedupedIds,
                ...Object.keys(draftSectionsByWallet[w]?.updatedDataBySectionId || {}).filter((sid) => {
                  if (!sid || sid === 'ReleaseVer') return false
                  const live = sectionsByWallet[w]?.[sid]
                  const draftData = draftSectionsByWallet[w]?.updatedDataBySectionId?.[sid]?.sectionData
                  if (!live || !draftData) return false
                  return !isEqual(draftData, live)
                }),
              ]),
            ]
            : dedupedIds

          upsertDraftSectionEntries({
            draftSection: draftSectionsByWallet[w],
            sectionsBySectionId: sectionsByWallet[w],
            originSectionsBySectionId: originSectionsByWallet[w],
            sectionIds: sectionIdsToUpsert,
            skipPreserveSortOrderDocIds: props.type === 'doc' && dragDocId ? [dragDocId] : [],
            preserveMissingCustomizedSortOrders: props.type !== 'master',
          })
        })
        console.log('changedSectionEntries', changedSectionEntries)
        console.log('draftSectionsByWallet', draftSectionsByWallet)
        await props.handleWriteDraftSection()
      }
      logDebugSortOrders('after-apply', {
        changedSectionEntries,
        dragSectionId: dragRef.sectionId,
        dropSectionId: dropRef.sectionId,
        dragDocId,
      })

      if (props.type === 'master') {
        displayedTreeData.value = treeData.value
      }

      const { sectionId: selSectionId, wallet: selWallet } = props.selectedSectionState
      if (selSectionId && selWallet && sectionsByWallet[selWallet]?.[selSectionId]) {
        const selectedInChangedEntries = changedSectionEntries
          .some(({ w, sId }) => w === selWallet && sId === selSectionId)
        const selectedInDraft = !!draftSectionsByWallet[selWallet]
          ?.updatedDataBySectionId?.[selSectionId]
        if (selectedInChangedEntries || selectedInDraft) {
          props.onSelectSectionState(sectionsByWallet[selWallet][selSectionId])
        }
      }

      props.onChangeSpinVisible(false)
    }

    const onOpenApiDoc = (dataRef) => {
      const { docId } = dataRef || {}
      const wallet = dataRef?.wallet || docId?.split('#')[1]
      const url = getApiDocLink({
        docId,
        wallet,
        docsDataByDocId: props.docsDataByDocId,
        isDev: isDev.value,
      })
      if (url) window.open(url, '_blank')
    }

    return {
      isDev,
      searchState,
      onSearch,
      expandedKeys,
      selectedKeys,
      displayedTreeData,
      onTreeExpand,
      onTreeSelect,
      onDuplicate,
      onDelete,
      onAddSection,
      onDrop,
      treeNodes,
      canMoveSectionToPeerWallet,
      onMoveSectionToPeerWallet,
      onOpenApiDoc,
    }
  },
}
</script>

<style scoped>
:deep(.section-tree) {
  padding: 0 16px;
  overflow-y: auto;
}

:deep(.section-tree .ant-tree-treenode) {
  width: 100%;
}

.menu-item-icon {
  color: #1890ff;
  margin-right: 4px;
}

.tree-title {
  font-weight: 600;
}

:deep(.delete-section-modal .ant-modal-confirm-content) {
  margin-top: 16px;
}
</style>
